import { useEffect, useState, useMemo } from 'react';
import { X, ImageIcon, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  listEntityImagesForArticle,
  type EntityImageRow,
} from '../../lib/api/articles.js';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/modal.js';
import { ImageLightbox, ZoomButton } from '../images/ImageLightbox.js';

type RoleFilter = 'all' | 'editorial_photo' | 'data_figure';

interface EntityImagePickerDialogProps {
  /** Article whose entity pool is being browsed. */
  articleId: string;
  /** Image being replaced — displayed in the header so the operator knows
   *  what's getting swapped out. */
  swappingImageId: string;
  swappingThumbUrl?: string | null;
  swappingRoleLabel?: string | null;
  /** Entity display name shown in the header (already known to the page,
   *  passed to avoid a second fetch). */
  entityDisplayName?: string | null;
  isOpen: boolean;
  onClose: () => void;
  /** Fired with the chosen image's id when operator clicks Apply. */
  /** Receives the whole row, not just the id: a STAGED replacement has to
   *  render the incoming image in the preview before anything is written. */
  onApply: (newImageId: string, image: EntityImageRow) => Promise<void> | void;
  /** True while the apply mutation is in flight (controlled by parent). */
  isApplying?: boolean;
}

export function EntityImagePickerDialog({
  articleId,
  swappingImageId,
  swappingThumbUrl,
  swappingRoleLabel,
  entityDisplayName,
  isOpen,
  onClose,
  onApply,
  isApplying,
}: EntityImagePickerDialogProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  // Preview index into `filtered`. Separate from `selectedId` on purpose:
  // looking at a candidate full-size must never choose it (operator
  // 2026-08-13 — "if I can't see a full size view without selecting it, I
  // can't tell if it's the one I want").
  const [zoomIndex, setZoomIndex] = useState<number | null>(null);

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['article-entity-images', articleId],
    queryFn: () => listEntityImagesForArticle(articleId, { limit: 50 }),
    enabled: isOpen,
    staleTime: 30_000,
  });

  // Reset selection when reopening / switching articles
  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
      setRoleFilter('all');
      setZoomIndex(null);
    }
  }, [isOpen]);

  const images = data?.images ?? [];

  // Counts for the filter pills BEFORE the role filter is applied
  // (helps the operator see how many options each pill exposes).
  const photoCount = useMemo(
    () => images.filter((i) => i.image_role === 'editorial_photo').length,
    [images],
  );
  const figureCount = useMemo(
    () => images.filter((i) => i.image_role === 'data_figure').length,
    [images],
  );

  const filtered = useMemo<EntityImageRow[]>(() => {
    if (roleFilter === 'all') return images;
    return images.filter((i) => i.image_role === roleFilter);
  }, [images, roleFilter]);

  if (!isOpen) return null;

  const apply = async () => {
    if (!selectedId) return;
    const row = images.find((i) => i.image_id === selectedId);
    if (!row) return;
    await onApply(selectedId, row);
  };

  const errorMsg = (() => {
    if (!isError) return null;
    const e = error as { message?: string } | null;
    return e?.message || 'Failed to load entity images';
  })();

  return (
    <Modal onClose={onClose} canClose={!isApplying} className="sm:max-w-5xl">
      {/* Header */}
      <ModalHeader>
          <div className="flex items-center space-x-3">
            <ImageIcon className="h-6 w-6 text-primary-600" />
            <div>
              <h2 className="text-xl font-bold text-grey-900">
                Replace image{entityDisplayName ? ` for ${entityDisplayName}` : ''}
              </h2>
              <p className="text-sm text-grey-600 mt-0.5">
                Swapping{' '}
                <span className="font-mono text-xs">
                  {swappingImageId.slice(0, 8)}…
                </span>
                {swappingRoleLabel && (
                  <>
                    {' '}({swappingRoleLabel})
                  </>
                )}
              </p>
            </div>
            {swappingThumbUrl && (
              <img
                src={swappingThumbUrl}
                alt="Currently in slot"
                className="h-12 w-12 object-cover rounded border border-grey-200 ml-2"
              />
            )}
          </div>
          <button
            onClick={onClose}
            disabled={isApplying}
            className="text-grey-400 hover:text-grey-600 transition-colors disabled:opacity-50 touch-target"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
      </ModalHeader>

        {/* Filter pills */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-grey-200 flex items-center gap-2 flex-wrap">
          <FilterPill
            label={`All (${images.length})`}
            active={roleFilter === 'all'}
            onClick={() => setRoleFilter('all')}
          />
          <FilterPill
            label={`Photos (${photoCount})`}
            active={roleFilter === 'editorial_photo'}
            onClick={() => setRoleFilter('editorial_photo')}
            disabled={photoCount === 0}
          />
          <FilterPill
            label={`Data figures (${figureCount})`}
            active={roleFilter === 'data_figure'}
            onClick={() => setRoleFilter('data_figure')}
            disabled={figureCount === 0}
          />
        </div>

        {/* Body */}
        <ModalBody>
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-16 text-grey-500">
              <Loader2 className="h-8 w-8 animate-spin mb-3" />
              <p>Loading entity images…</p>
            </div>
          )}
          {errorMsg && !isLoading && (
            <div className="p-4 bg-danger-50 border border-danger-200 text-danger-700 rounded-lg">
              <p className="font-semibold">Could not load images</p>
              <p className="text-sm mt-1">{errorMsg}</p>
            </div>
          )}
          {!isLoading && !errorMsg && data?.warning && (
            <div className="p-4 bg-warning-50 border border-warning-200 text-warning-700 rounded-lg mb-4">
              <p className="text-sm">{data.warning}</p>
            </div>
          )}
          {!isLoading && !errorMsg && filtered.length === 0 && (
            <div className="text-center py-16 text-grey-500">
              <p className="font-semibold">No swappable images in this entity's pool.</p>
              <p className="text-sm mt-2">
                Personnel / table / decorative images are filtered out, and images
                already used by this article aren't shown.
              </p>
            </div>
          )}
          {!isLoading && filtered.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map((img, i) => (
                <ImageTile
                  key={img.image_id}
                  image={img}
                  selected={selectedId === img.image_id}
                  onSelect={() => setSelectedId(img.image_id)}
                  onZoom={() => setZoomIndex(i)}
                />
              ))}
            </div>
          )}
        </ModalBody>

        {/* Footer */}
        <ModalFooter className="bg-grey-50 sm:rounded-b-xl">
          <button
            onClick={onClose}
            disabled={isApplying}
            className="px-4 py-2 text-sm font-medium text-grey-700 hover:text-grey-900 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={apply}
            disabled={!selectedId || isApplying}
            className="px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isApplying && <Loader2 className="h-4 w-4 animate-spin" />}
            Apply
          </button>
        </ModalFooter>

        {zoomIndex !== null && filtered[zoomIndex] && (
          <ImageLightbox
            items={filtered.map((img) => ({
              // The tile shows a thumbnail; the preview must show the
              // original — reverse of the tile's URL preference.
              url: img.cdn_url || img.hero_url || img.inline_url || img.thumbnail_url || '',
              alt: img.ai_description?.suggested_alt_text ?? null,
              title: img.ai_description?.suggested_caption
                || img.ai_description?.description
                || img.image_type
                || null,
              subtitle: [
                img.image_role ?? 'no role',
                img.image_type,
                typeof img.quality_score === 'number' ? `q${img.quality_score.toFixed(2)}` : null,
                img.is_hero_eligible === false ? 'hero blocked' : null,
              ].filter(Boolean).join(' · '),
            }))}
            index={zoomIndex}
            onIndex={setZoomIndex}
            onClose={() => setZoomIndex(null)}
          />
        )}
    </Modal>
  );
}

function FilterPill({
  label,
  active,
  onClick,
  disabled,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={
        'px-3 py-1.5 text-sm rounded-full border transition-colors ' +
        (active
          ? 'bg-primary-100 border-primary-300 text-primary-800 font-semibold'
          : 'bg-surface border-grey-300 text-grey-700 hover:bg-grey-50') +
        (disabled ? ' opacity-50 cursor-not-allowed' : '')
      }
    >
      {label}
    </button>
  );
}

function ImageTile({
  image,
  selected,
  onSelect,
  onZoom,
}: {
  image: EntityImageRow;
  selected: boolean;
  onSelect: () => void;
  onZoom: () => void;
}) {
  const src =
    image.thumbnail_url || image.inline_url || image.cdn_url || image.hero_url;
  const desc =
    image.ai_description?.description ||
    image.ai_description?.suggested_caption ||
    '';
  const truncated = desc.length > 80 ? desc.slice(0, 80) + '…' : desc;
  return (
    <div className="group relative">
    <ZoomButton onClick={onZoom} />
    <button
      onClick={onSelect}
      type="button"
      className={
        'w-full text-left rounded-lg border-2 overflow-hidden transition-all bg-surface ' +
        (selected
          ? 'border-primary-500 ring-2 ring-primary-200'
          : 'border-grey-200 hover:border-grey-400')
      }
    >
      {src ? (
        <img
          src={src}
          alt={image.ai_description?.suggested_alt_text || image.image_type || 'image'}
          className="w-full aspect-video object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full aspect-video bg-grey-100 flex items-center justify-center text-grey-400">
          <ImageIcon className="h-8 w-8" />
        </div>
      )}
      <div className="p-3 space-y-1.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={
              'inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wide ' +
              (image.image_role === 'editorial_photo'
                ? 'bg-success-50 text-success-700'
                : image.image_role === 'data_figure'
                ? 'bg-primary-50 text-primary-700'
                : 'bg-grey-100 text-grey-700')
            }
          >
            {image.image_role || 'unknown'}
          </span>
          <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-grey-100 text-grey-700 uppercase">
            {image.image_type || 'image'}
          </span>
          {typeof image.quality_score === 'number' && (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-grey-50 text-grey-600">
              q {image.quality_score.toFixed(2)}
            </span>
          )}
        </div>
        {truncated && (
          <p className="text-xs text-grey-600 leading-snug line-clamp-2">
            {truncated}
          </p>
        )}
      </div>
    </button>
    </div>
  );
}
